// src/pages/Inventory/InventoryPage.tsx

// GENERAL
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// COMPONENTS
import { BaseButton, BaseText } from "../../../components/ui";
import Section from "../../../components/layout/Section/Section";
import GroupModal from "../view/groupBymodal/GroupModal";
import { PrimaryTable } from "../../../components/ui/Tables";
import {
	RefreshButton,
	ResetFiltersButton,
} from "../../../components/ui/Tables/PrimaryTable";

// OPTIONS FIELDS
import { groupOptions, inventoryPageFields } from "../viewModel";

// UTILS & HELPERS
import { getFontAwesomeIcon } from "../../../utils";
import { createApplyGrouping } from "../helpers";
import getPageSlug from "../../../utils/getPageSlug";
import getTableViewSlug from "../../../utils/getTableViewSlug";
// HOOKS
import { PageDataProvider } from "../../../hooks/usePageDataContext";
import { DataTableContextProvider } from "../../../components/ui/Tables/hooks/useDataTableContext";
import { useTableStore } from "../../../stores";
import usePageListDetails from "../../../hooks/usePageListDetails";
import useSeedInventoryQuery from "../hooks/useSeedInventoryQuery";
import { useDeepLinkSubTable } from "../../../hooks";

// TYPES
import {
	TableDisplayData,
	AdditionalData,
} from "../../../components/ui/Tables/types";

function capitalizeFirstLetter(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

const InventoryPage: React.FC = () => {
	const queryParams = new URLSearchParams(location.search);

	// DEFAULT TABLE VIEW
	const defaultOption =
		groupOptions.find((o) => o.isDefault) || groupOptions[0];

	const pageSlug = getPageSlug() || defaultOption.pageSlugs.pageSlug;
	const tableSlug =
		getTableViewSlug() || defaultOption.tableSlugs.primaryTableSlug;
	const defaultOptionUuid = defaultOption.uuid;

	const pageTitle = pageSlug;
	const paramName = `${tableSlug}:group`;
	const paramUuid = queryParams.get(paramName) || defaultOptionUuid;
	const { pageMeta } = usePageListDetails(pageSlug);

	const currentTableView = groupOptions.find(
		(groupOption) => groupOption.uuid === paramUuid
	)!;

	const hasTableRowExpansion = currentTableView.tableSlugs.nestedTableSlug
		? true
		: false;

	useSeedInventoryQuery(
		currentTableView.uuid,
		pageSlug,
		tableSlug,
		currentTableView.uuid
	);

	const navigate = useNavigate();
	const { setSlug } = useTableStore();
	const { search, pathname } = useLocation();

	const deepLinkNav = useDeepLinkSubTable();

	const [loading, setLoading] = useState<boolean>(false);
	const [resetKey, setResetKey] = useState<number>(0);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [additionalData, setAdditionalData] = useState<AdditionalData>(
		{} as AdditionalData
	);

	const [tableDisplayData, setTableDisplayData] = useState<TableDisplayData>(
		{} as TableDisplayData
	);
	const [isAdditionalDataLoaded, setIsAdditionalDataLoaded] =
		useState<boolean>(false);
	const [appliedGroup, setAppliedGroup] = useState<string>(tableSlug);
	const [pendingGroup, setPendingGroup] = useState<string>(appliedGroup);

	// PAGE SETTINGS VIA DUMMY FIELDS
	const isGroupByModalVisible =
		inventoryPageFields.page.isGroupByModalVisible;
	const isResetFiltersButtonVisible =
		inventoryPageFields.page.isResetFiltersButtonVisible;

	useEffect(() => {
		const params = new URLSearchParams(search);
		const match = groupOptions.find(
			(opt) => params.get(`${tableSlug}:group`) === opt.uuid
		);
		if (match) {
			setAppliedGroup(match.uuid);
			setPendingGroup(match.uuid);
		}
	}, [search]);

	useEffect(() => {
		// Set the default grouping in local storage if it doesn't already exist
		if (!localStorage.getItem("selectedGrouping")) {
			localStorage.setItem(
				"selectedGrouping",
				JSON.stringify(defaultOption)
			);
		}
	}, []);
	const applyGrouping = createApplyGrouping(
		navigate,
		pathname,
		search,
		paramName,
		groupOptions,
		setAppliedGroup,
		setPendingGroup,
		setIsModalOpen
	);

	const openModal = () => {
		setPendingGroup(appliedGroup);
		setIsModalOpen(true);
	};

	// URL keys are declared in the group options fields
	// These are being used to tell the useFetchTableData hook which primary record key to use, according to what is in the url
	const urlKeys = groupOptions?.reduce(
		(acc: { [key: string]: any }[], option: { [key: string]: any }) => {
			if (option.urlKeys && option.urlKeys !== null) {
				acc.push(option.urlKeys);
			}
			return acc;
		},
		[]
	);
	localStorage.setItem("urlKeys", JSON.stringify(urlKeys));

	useEffect(() => {
		let additionalData: AdditionalData = {};
		let tableDisplayData: TableDisplayData = {};

		// VIEW: Customer POs By Items
		if (currentTableView.uuid == "904cc7b1-ad50-426b-b64f-87bb23d0a93a") {
			tableDisplayData =
				currentTableView.tableDisplayData as TableDisplayData;
		} else if (
			//VIEW: Items By Customer POs
			currentTableView.uuid == "94f1e621-6eb4-bece-7f67-302ed49bce88"
		) {
			additionalData = {
				slug: "customerId",
				uuid: "1",
			};

			tableDisplayData =
				currentTableView.tableDisplayData as TableDisplayData;
		}
		setAdditionalData(additionalData);
		setTableDisplayData(tableDisplayData);
		setIsAdditionalDataLoaded(true);

		// also ensure setSlug is called once (you can do this here or in its own effect)
		setSlug(pageSlug);
	}, [search, setSlug]);

	const handleTableRefresh = () => {
		const params = new URLSearchParams(search);
		params.delete(`${tableSlug}_page`);
		navigate({ pathname, search: params.toString() }, { replace: false });
		setResetKey((prev) => prev + 1);
	};

	const currentGroupViewOptionsButtonText = `${currentTableView.label}`;

	const selectedGrouping = localStorage.getItem("selectedGrouping");
	const secondaryTableViewSlug = selectedGrouping
		? JSON.parse(selectedGrouping).tableSlugs?.nestedTableSlug || null
		: null;

	const deepLinkCellAction = (_e: React.MouseEvent, primaryUuid: string) => {
		sessionStorage.setItem("inventoryLastUrl", window.location.href);
		// 1) pass our flag into the new entry’s state
		navigate(pathname + search, {
			replace: false,
			state: { hasSubTable: true },
		});

		// 2) your existing logic, untouched
		const params = new URLSearchParams(search);
		const groupUuid = params.get(`${tableSlug}:group`)!;

		if (secondaryTableViewSlug === "items-for-purchase-orders") {
			const secondaryUuid = params.get(
				`${tableSlug}:sales-orders-item-uuid`
			)!;
			deepLinkNav(
				groupUuid,
				primaryUuid,
				secondaryUuid,
				"sales-orders-item-uuid",
				"units"
			);
		} else if (secondaryTableViewSlug === "purchase-orders_items") {
			const secondaryUuid = params.get(
				`${tableSlug}:items-for-purchase-orders`
			)!;
			deepLinkNav(
				groupUuid,
				primaryUuid,
				secondaryUuid,
				"items-for-purchase-orders",
				"units"
			);
		}
	};

	return (
		<div className="px-[60px] h-full font-omnes-regular">
			<Section additionalClasses="py-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center">
						{getFontAwesomeIcon("boxesStacked", "solid")}
						<BaseText
							text={capitalizeFirstLetter(pageTitle)}
							additionalClasses="pl-2"
							size="text-22px"
							color="text-navy-500"
							tag="h1"
							fontFamily="font-omnes-medium"
						/>
					</div>
					<div className="flex ">
						{isGroupByModalVisible && (
							<div className="mx-2 ">
								<BaseButton
									text={currentGroupViewOptionsButtonText}
									bgColor={"bg-white"}
									fontColor="text-navy-500"
									buttonSize="h-26px"
									shape="rounded"
									icon={
										<span className="text-white bg-primary rounded text-xs p-0.5">
											{getFontAwesomeIcon(
												"layerGroup",
												"solid"
											)}
										</span>
									}
									iconBreakpoint="max-sm:pr-0"
									additionalClasses="shadow-filter-group hover:bg-supply-blue-50 border-1 border-navy-200 text-nowrap max-sm:size-8 max-sm:justify-center text-14px px-2 py-1"
									as="button"
									textBreakpoint="max-sm:hidden"
									onClick={openModal}
									fontFamily="font-omnes-medium"
									iconClasses="pr-2"
								/>
							</div>
						)}

						<div className="h-8 w-[1px] bg-navy-250 "></div>
						<div className="mx-2 flex gap-4">
							<RefreshButton
								loading={loading}
								onClick={handleTableRefresh}
							/>

							{isResetFiltersButtonVisible && (
								<ResetFiltersButton slug={pageSlug} />
							)}
						</div>
					</div>
				</div>
			</Section>
			{isAdditionalDataLoaded && (
				<PageDataProvider pageMeta={pageMeta} pageTitle={pageTitle}>
					<DataTableContextProvider
						showStickyColumn={false}
						hasInfiniteScroll={tableDisplayData?.hasInfiniteScroll}
						hasModalTable={false}
						rowsHaveModalOnClick={false}
						data={[]}
						key={resetKey}
					>
						<PrimaryTable
							pageTitle={pageTitle}
							hasModalTable={false}
							setPageTitle={() => {}}
							setHasModalTable={() => {}}
							setData={() => {}}
							additionalData={additionalData}
							tableDisplayData={tableDisplayData}
							slug={tableSlug}
							setIsTableLoading={setLoading}
							skipNavigateToPreviousUrl={false}
							viewSlug={tableSlug}
							callClickAction={deepLinkCellAction}
							hasTableRowExpansion={hasTableRowExpansion}
						/>
					</DataTableContextProvider>
				</PageDataProvider>
			)}
			{isGroupByModalVisible && (
				<GroupModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					groupOptions={groupOptions}
					selectedValue={pendingGroup}
					onGroupChange={setPendingGroup}
					onApply={applyGrouping}
					viewSlug={tableSlug}
				/>
			)}
		</div>
	);
};

export default InventoryPage;
